import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, List, Modal, Row, Tooltip, notification } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import { Empty, PageHeader } from 'src/components/Ant'
import { getById } from 'src/services/api/users'
import {
  getContracts,
  getCostCenters,
  getReasons
} from 'src/services/api/services'
import { RequestTicket, RequestToClassify } from 'src/types'
import { resetAir } from 'src/core/store/ducks/air/actions'
import { resetHotels } from 'src/core/store/ducks/hotels/actions'
import { resetVehicles } from 'src/core/store/ducks/vehicles/actions'
import { setDraft } from 'src/core/store/ducks/requests/actions'
import * as RequestService from 'src/services/api/requests'
import history from 'src/config/routes/history'
import useDocumentTitle from 'src/hooks/use-document-title'
import * as Css from './styles'

export type CheckedTickets = RequestTicket & { checked: boolean }

function ListHeader() {
  const {
    me: {
      user: { auditor }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  const { t } = useTranslation()
  return (
    <div>
      <h2>{t('web.request-to-classify.section-description')}</h2>
      <br />
      <Css.ListItem style={{ paddingBottom: '1px' }}>
        <div className="sequence">{t('web.table.head.id')}</div>
        <div className="dates">{t('common.period')} </div>
        <div className="description">{t('web.resources.reasons.title')}</div>
        <div className="description">
          {t('web.assignment.sharing.form.cost-center.title')}
        </div>
        <div className="description">
          {t('web.table.head-partner.description.name')}
        </div>
        {auditor && <div className="sequence"></div>}
      </Css.ListItem>
    </div>
  )
}

export default function RequestClassification() {
  const {
    me: {
      user: { user_id, organization_id, auditor }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    resources: state.resources
  }))

  useDocumentTitle('web.request-to-classify.title')

  const isSubscribed = useRef(true)
  const [isLoading, setIsLoading] = useState(false)
  const [requestList, setRequestList] = useState<RequestToClassify[]>([])
  const dispatch = useDispatch()
  const { t } = useTranslation()

  function updateList() {
    setIsLoading(true)
    RequestService.GetRequestsToClassify({ organization_id, user_id })
      .then(response => {
        if (!isSubscribed.current) return

        if (response.status === 200) {
          setRequestList(response.data)
          return
        }

        notification.error({
          duration: 8,
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .catch(response => {
        if (!isSubscribed.current) return
        notification.error({
          duration: 8,
          message:
            typeof response.data?.reasons === 'string'
              ? response.data?.reasons
              : t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (!isSubscribed.current) return
        setIsLoading(false)
      })
  }

  useEffect(() => {
    dispatch(resetAir())
    dispatch(resetHotels())
    dispatch(resetVehicles())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PageHeader
        onBack={() => history.goBack()}
        title="web.request-to-classify.title"
      />
      <Row>
        <Col>
          <Css.Card>
            <List
              dataSource={requestList}
              header={<ListHeader />}
              loading={isLoading}
              locale={{ emptyText: <Empty /> }}
              renderItem={(request, index) => (
                <Css.ListItem
                  key={request.request_base_id}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (!request.reason_id) {
                      Modal.error({
                        content: t('common.response.unexpected-error')
                      })
                      return
                    }

                    Modal.confirm({
                      cancelText: t('common.actions.cancel'),
                      content: t('web.request-to-classify.confirm-new'),
                      okText: t('common.actions.yes'),
                      onCancel() {},
                      onOk: async () => {
                        dispatch(setDraft(null))
                        const request_for_classification = Object.assign(
                          {},
                          request
                        ) as RequestToClassify

                        if (request.cost_center_id) {
                          const costCenterList = await getCostCenters({
                            organization_id: request.organization_id,
                            search: request.cost_center_code
                          })

                          const costCenter = costCenterList?.data?.find(
                            c => c.cost_center_id === +request.cost_center_id
                          )
                          if (!costCenter) {
                            Modal.error({
                              title: t(
                                'web.request-to-classify.cost-center-not-found'
                              )
                            })
                            return
                          }
                          request_for_classification.costCenter = costCenter
                        }

                        if (request.reason_id) {
                          const reasonsList = await getReasons({
                            organization_id: request.organization_id
                          })
                          const reason = reasonsList?.data?.find(
                            c => c.reason_id === +request.reason_id
                          )
                          if (!reason) {
                            Modal.error({
                              title: t(
                                'web.request-to-classify.reason-not-found'
                              )
                            })
                            return
                          }
                          request_for_classification.reason = reason
                        }

                        const contractList = await getContracts({
                          organization_id: request.organization_id
                        })

                        const contract = contractList?.data?.find(
                          c => c.standard
                        )

                        if (!contract) {
                          Modal.error({
                            title: t(
                              'web.request-to-classify.contract-not-found'
                            )
                          })
                          return
                        }

                        request_for_classification.contract = contract
                        if (request.user_id) {
                          const responseUser = await getById({
                            user_id: request.user_id,
                            withInactive: true
                          })

                          const user = responseUser.data
                          request_for_classification.user = user
                        }

                        history.push(
                          `/my-requests/create/reason/${request.reason_id}`,
                          {
                            request_for_classification
                          }
                        )
                      },
                      title: t('common.are-you-sure')
                    })
                  }}
                >
                  <div className="sequence">{index + 1}</div>
                  <div className="dates">
                    <span>{`${moment
                      .utc(request.requested_on * 1000)
                      .format('DD/MM/YY')}
                      ${
                        request.date_end
                          ? `- ${moment
                              .utc(request.date_end * 1000)
                              .format('DD/MM/YY')}`
                          : ''
                      }
                      `}</span>
                  </div>
                  <div className="description">{`${request.reason_code} - ${request.reason_name}`}</div>
                  <div className="description">{`${request.cost_center_code} - ${request.cost_center_name}`}</div>
                  <div className="description">
                    <Tooltip title={request.title}>{request.title}</Tooltip>
                  </div>
                  {auditor && (
                    <div className="sequence">
                      <Button
                        icon="delete"
                        onClick={e => {
                          e.stopPropagation()
                          Modal.confirm({
                            cancelText: t('common.actions.cancel'),
                            content: t('web.request.exclude'),
                            okText: t('common.actions.yes'),
                            onCancel() {},
                            onOk: async () => {
                              const response =
                                await RequestService.deleteRequestsToClassify({
                                  request_base_id: request.request_base_id,
                                  organization_id: request.organization_id
                                })

                              if (response.status !== 200) {
                                const message =
                                  typeof response.data.detail === 'string'
                                    ? response.data.detail
                                    : t('common.response.status-500')

                                notification.error({
                                  duration: 8,
                                  message,
                                  placement: 'bottomRight'
                                })
                                return
                              }

                              updateList()
                            },
                            title: t('common.are-you-sure')
                          })
                        }}
                        type="danger"
                      />
                    </div>
                  )}
                </Css.ListItem>
              )}
            />
          </Css.Card>
        </Col>
      </Row>
    </>
  )
}
